import {
  ArrowSquareOut,
  Briefcase,
  CreditCard,
  Eye,
  FlagBannerFold,
  GearFine,
  Layout,
  Palette,
  PlusCircle,
  Receipt,
  User,
  UserCircleDashed,
  Users,
  WebhooksLogo,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import InviteMemberModal from "common/components/InviteMemberModal";
import { Button } from "common/components/ui/Button";
import WorkspaceSelector from "common/components/WorkspaceSelector";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceToken from "common/datahooks/useWorkspaceToken";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import Banner from "common/layouts/WorkspaceLayout/Banners/Banner";
import type { UserPermissions } from "common/types";

import AgencyInvoices from "../components/AgencyPlan/AgencyInvoices";
import GetAgencyPlan from "../components/AgencyPlan/GetAgencyPlan";
import PlanDetails from "../components/AgencyPlan/PlanDetails";
import ApiKeys from "../components/ApiKeys";
import CreateApiTokenButton from "../components/ApiKeys/CreateApiTokenButton";
import Billing from "../components/Billing";
import Clients from "../components/Clients";
import NewClientButton from "../components/Clients/NewClientButton";
import General from "../components/General";
import LifetimeSeats from "../components/LifetimeSeats";
import Members from "../components/Members";
import NonAgencyInvoices from "../components/NonAgencyInvoices";
import ProfileSettings from "../components/ProfileSettings";
import Webhooks from "../components/Webhooks";
import NewWebhookButton from "../components/Webhooks/NewWebhookButton";
import Whitelabel from "../components/Whitelabel";
import { WHITELABEL_DOMAIN } from "../constants";
import useAgencySettings from "../datahooks/useAgencySettings";
import useWhitelabelPreviewUrl from "./useWhitelabelPreviewUrl";

import type { JSX, ReactNode } from "react";

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  component: JSX.Element;
  actionComponent?: JSX.Element;
  visibleForPermissions?: UserPermissions;
  bannerComponent?: JSX.Element;
}
interface NavItemWithSubNav extends Omit<NavItem, "component"> {
  subNavItems: Omit<NavItem, "icon" | "visibleForPermissions">[];
}
interface NavItemComponent {
  element: ReactNode;
}

export default function useSettingsNavItems() {
  const isDesktop = useTwBreakpoint("lg");

  const { permissions, role } = useSelectedWorkspaceContext();
  const { workspaceToken } = useWorkspaceToken();
  const { agencyPlan, appSumoPromotion, ltdPromotion } = usePromotions();
  const { agencySettings } = useAgencySettings();
  const { whitelabelCustomization } = getWhitelabelCustomization();
  const { whitelabelPreviewUrl, refreshWhitelabelPreviewUrl } =
    useWhitelabelPreviewUrl(
      agencySettings ? `${agencySettings.slug}.${WHITELABEL_DOMAIN}` : "",
    );

  const actionButtonSize = isDesktop ? "sm" : "md";

  let navItems: (NavItem | NavItemWithSubNav | NavItemComponent)[] = [
    {
      path: "",
      label: "Profile settings",
      icon: <User weight="fill" />,
      component: <ProfileSettings />,
    },
  ];
  if (!whitelabelCustomization) {
    if (isDesktop) {
      navItems.unshift({ element: <WorkspaceSelector isExpanded /> });
    }
    navItems.push(
      {
        path: "general",
        label: "General",
        icon: <Layout weight="fill" />,
        component: <General />,
      },
      {
        path: "billing",
        label: "Billing",
        icon: <CreditCard weight="fill" />,
        component: <Billing />,
        visibleForPermissions: ["billing_full", "billing_limited"],
      },
      {
        path: "invoices",
        label: "Invoices",
        icon: <Receipt weight="fill" />,
        component: <NonAgencyInvoices />,
        visibleForPermissions: ["billing_full", "billing_limited"],
      },
      {
        path: "members",
        label: "Members",
        icon: <Users weight="fill" />,
        component: <Members />,
        visibleForPermissions: ["members_full", "members_limited"],
        actionComponent: (
          <InviteMemberModal
            buttonComponent={
              <Button
                leftIcon={<PlusCircle weight="fill" />}
                size={actionButtonSize}
              >
                Invite teammate
              </Button>
            }
          />
        ),
      },
    );
    if (agencyPlan && role === "owner") {
      navItems.push({
        path: "clients",
        label: "Clients",
        icon: <UserCircleDashed weight="fill" />,
        component: <Clients />,
        actionComponent: <NewClientButton size={actionButtonSize} />,
        bannerComponent: !agencySettings && (
          <Banner variant="warning">
            <p className="lg:truncate">
              You must configure your whitelabel branding before inviting
              clients
            </p>
            <Button
              variant="link-black"
              className="ml-auto lg:border-l border-black-200"
              asChild
            >
              <Link to="./whitelabel">Go to whitelabel settings</Link>
            </Button>
          </Banner>
        ),
      });
    }
  }
  if (!whitelabelCustomization) {
    navItems.push(
      {
        path: "api",
        label: "API keys",
        icon: <GearFine weight="fill" />,
        component: <ApiKeys />,
        visibleForPermissions: ["integrations_full"],
        actionComponent: (
          <div className="flex gap-x-2.5">
            <Button
              variant="secondary-black"
              className="flex-1"
              asChild
              leftIcon={<ArrowSquareOut />}
              size={actionButtonSize}
            >
              <a
                href="https://docs.aimfox.com/"
                target="_blank"
                rel="noreferrer"
              >
                Documentation
              </a>
            </Button>
            <CreateApiTokenButton size={actionButtonSize} />
          </div>
        ),
      },
      {
        path: "webhooks",
        label: "Webhooks",
        icon: <WebhooksLogo weight="fill" />,
        component: <Webhooks />,
        visibleForPermissions: ["webhooks_full"],
        actionComponent: (
          <div className="flex gap-x-2.5">
            <Button
              variant="secondary-black"
              leftIcon={<ArrowSquareOut />}
              size={actionButtonSize}
              asChild
            >
              <a
                href="https://docs.aimfox.com/webhooks"
                target="_blank"
                rel="noreferrer"
              >
                Documentation
              </a>
            </Button>
            <NewWebhookButton size={actionButtonSize} />
          </div>
        ),
      },
    );
  }

  if (!whitelabelCustomization) {
    if (role === "owner") {
      navItems.push({
        element: <div className="lg:w-full lg:h-px bg-black-200 mb-1 mt-2" />,
      });
      const agencyPlanBase = {
        path: "agency",
        label: "Agency plan",
        icon: <Briefcase weight="fill" />,
      };
      if (agencyPlan) {
        navItems.push({
          ...agencyPlanBase,
          subNavItems: [
            {
              path: "",
              label: "Manage",
              component: <PlanDetails />,
              actionComponent: <div />,
            },
            {
              path: "invoices",
              label: "Invoices",
              component: <AgencyInvoices />,
              actionComponent: <div />,
            },
          ],
        });
        navItems.push({
          path: "whitelabel",
          label: "White label",
          icon: <Palette weight="fill" />,
          component: <Whitelabel />,
          actionComponent: agencySettings && (
            <div className="flex gap-x-2.5">
              <Button
                variant="outline-black"
                leftIcon={<Eye />}
                asChild
                size={actionButtonSize}
              >
                <a
                  target="_blank"
                  href={whitelabelPreviewUrl}
                  rel="noreferrer"
                  onClick={refreshWhitelabelPreviewUrl}
                >
                  Preview white label
                </a>
              </Button>
              <Button
                variant="outline-black"
                leftIcon={<Eye />}
                size={actionButtonSize}
                asChild
              >
                <a
                  target="_blank"
                  href={`${import.meta.env.VITE_WHITE_LABEL_URL}${workspaceToken ? `#${workspaceToken}` : ""}`}
                  rel="noreferrer"
                >
                  Preview login
                </a>
              </Button>
            </div>
          ),
        });
      } else {
        navItems.push({
          ...agencyPlanBase,
          component: <GetAgencyPlan />,
        });
      }
    }
    if (role === "owner" && (ltdPromotion || appSumoPromotion)) {
      navItems.push({
        path: "ltd",
        label: "Lifetime seats",
        icon: <FlagBannerFold weight="fill" />,
        component: <LifetimeSeats />,
      });
    }
  }

  //filter items based on permissions
  navItems = navItems.filter((item) => {
    if ("element" in item) {
      return true;
    }
    const { visibleForPermissions } = item;
    return visibleForPermissions
      ? visibleForPermissions.some((permission) =>
          permissions.includes(permission),
        )
      : true;
  });

  const routes = navItems.filter((item) => !("element" in item)) as (
    | NavItemWithSubNav
    | NavItem
  )[];

  return { navItems, routes };
}
